import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Collapse,
  IconButton
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { styled } from '@mui/material/styles';

const ExpandButton = styled((props) => {
  const { expand, ...other } = props;
  return <IconButton {...other} />;
})(({ theme, expand }) => ({
  transform: !expand ? 'rotate(0deg)' : 'rotate(180deg)',
  marginLeft: 'auto',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}));

const CoreConceptsGrid = ({ inputInfo, userInfo, retireYearData }) => {
  const [expandedCard, setExpandedCard] = useState(null);

  const cards = [
    {
      id: 'distribution-planning',
      title: 'Distribution Planning',
      description: 'An imperative first step in planning is decideing how our retirement account(s) will be distributed, since the goal of Roth conversions is to maximize the after-tax value of those distributions.',
      detailedContent: (
        <>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Every retirement account is eventually distributed in full and those distributions will all be taxed.  
            The weighted tax rate on these distributions <em>is</em> the <strong>true Distribution Tax Rate</strong>.
          </Typography>
          
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Selecting the 'Fixed Annuity' Distribution Method produces equal annual distributions during the investors selected life period that fully depletes the account by the end of that period. 
            This is an excellent schedule for people who need the steady income every year that their retirement savings can provide.
            Selecting the 'RMD Only' Distribution Method is for those who do not need the yearly income and wish to maximize account value left to heirs. 
            This method includes a 10 year heir distribution period before the account is fully depleted.
          </Typography>
        </>
      ),
      color: '#e3f2fd',
      borderColor: '#b3e5fc'
    },
    {
      id: 'tax-arbitrage',
      title: 'Tax-Rate Arbitrage: Core Conversion Principle',
      description: 'Convert when: Conversion tax rate < Tax rate on future distributions',
      detailedContent: (
        <>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            Convert $100,000 today at a 12% tax rate to avoid paying the 20% tax rate on future distributions. The arbitrage: you pay $12,000 now to eliminate the $20,000 tax liability on the $100,000.
            This $8,000 profit ($20,000 - $12,000) is an immediate $8,000 increase in wealth.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 2 }}>
            This $8,000 wealth increase is realized whether the tax is paid from within the Traditional account or from funds outside the account. Since we can earn the immediate $8,000 arbitrage profit
            without writing a check, the inside-funded conversion may be an attractive option for people aged 59 1/2 and older.
          </Typography>
          <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
            The $8,000 profit starts growing at the portfolio's rate of return as soon as the conversion is completed. Important note: if your conversion produces a loss (conversion rate &gt; distribution rate), that loss also grows over time.
          </Typography>
        </>
      ),
      color: '#e8f5e9',
      borderColor: '#c8e6c9'
    }
  ];

  const handleCardClick = (cardId) => {
    setExpandedCard(expandedCard === cardId ? null : cardId);
  };

  return (
    <Box sx={{ mb: 1 }}>
      <Typography variant="h6" sx={{ mb: 0, fontWeight: 'bold', color: 'primary.main' }}>
        Core Concepts
      </Typography>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
        gap: 3
      }}>
        {cards.map((card) => (
          <Paper
            key={card.id}
            elevation={2}
            sx={{
              px: 1.5,
              py: 0.25,
              backgroundColor: card.color,
              border: `1px solid ${card.borderColor}`,
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              '&:hover': {
                elevation: 4,
                transform: 'translateY(-2px)'
              }
            }}
            onClick={() => handleCardClick(card.id)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                {card.title}
              </Typography>
              <ExpandButton
                expand={expandedCard === card.id}
                aria-expanded={expandedCard === card.id}
                aria-label="show more"
              >
                <ExpandMoreIcon />
              </ExpandButton>
            </Box>

            <Typography variant="body1" sx={{ lineHeight: 1.7, mb: 0.5 }}>
              {card.description}
            </Typography>

            <Collapse in={expandedCard === card.id} timeout="auto" unmountOnExit>
              <Box sx={{
                mt: 2,
                pt: 2,
                borderTop: `2px solid ${card.borderColor}`
              }}>
                <Box>
                  {card.detailedContent}
                </Box>
              </Box>
            </Collapse>
          </Paper>
        ))}
      </Box>

    </Box>
  );
};

export default CoreConceptsGrid;
